import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Chip,
    Tooltip,
    Alert,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    PictureAsPdf as PdfIcon,
    Article as ArticleIcon,
    TableChart as SpreadsheetIcon,
    Slideshow as SlideshowIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { isStaffRole, isMasterAdmin as isMasterAdminRole, isSchoolScopedRole, isTeacher } from '../../constants/roles';

// ─── File type helpers ───────────────────────────────────────────────────────
const FILE_TYPE_CONFIG = {
    'application/pdf': { icon: <PdfIcon />, label: 'PDF', color: '#e53935' },
    'application/msword': { icon: <ArticleIcon />, label: 'Word', color: '#4338ca' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: <ArticleIcon />, label: 'Word', color: '#4338ca' },
    'application/vnd.ms-excel': { icon: <SpreadsheetIcon />, label: 'Excel', color: '#2e7d32' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: <SpreadsheetIcon />, label: 'Excel', color: '#2e7d32' },
    'application/vnd.ms-powerpoint': { icon: <SlideshowIcon />, label: 'PPT', color: '#e65100' },
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: <SlideshowIcon />, label: 'PPT', color: '#e65100' },
    'text/plain': { icon: <DescriptionIcon />, label: 'Text', color: '#616161' },
};

function getFileTypeInfo(contentType) {
    return FILE_TYPE_CONFIG[contentType] || { icon: <DescriptionIcon />, label: 'File', color: '#616161' };
}

function formatFileSize(bytes) {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(isoString) {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
}

// ─── CurriculumPage component ────────────────────────────────────────────────
const CurriculumPage = ({ currentUser, currentUserRole }) => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [schoolFilter, setSchoolFilter] = useState('');
    const [classFilter, setClassFilter] = useState('');

    // DB-backed school / class lists
    const [availableSchools, setAvailableSchools] = useState([]);
    const [availableClasses, setAvailableClasses] = useState([]);

    // Upload dialog state
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadSchool, setUploadSchool] = useState('');
    const [uploadClass, setUploadClass] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Document viewer
    const [viewDoc, setViewDoc] = useState(null);
    const [viewUrl, setViewUrl] = useState(null);
    const [viewText, setViewText] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState(null);

    // "Admin" here means "can upload/delete curriculum" — a teacher can too,
    // scoped to their own classes server-side, so it includes TEACHER
    // alongside the two admin roles.
    const isAdmin = isStaffRole(currentUserRole);
    const isMasterAdmin = isMasterAdminRole(currentUserRole);
    // SCHOOL_ADMIN and TEACHER both belong to exactly one school: the
    // upload dialog's School field is a read-only chip for them instead of
    // a free-pick dropdown, same as everywhere else in the dashboard.
    const isSchoolScoped = isSchoolScopedRole(currentUserRole);
    const isTeacherRole = isTeacher(currentUserRole);

    // ── Fetch schools from DB ────────────────────────────────────────────────
    useEffect(() => {
        authFetch(`${config.API_BASE_URL}/api/curriculum/schools`)
            .then((r) => handleApiResponse(r, navigate))
            .then((data) => { if (data) setAvailableSchools(data.schools || []); })
            .catch(() => {});
    }, [navigate]);

    // ── Fetch classes from DB whenever upload school changes ─────────────────
    useEffect(() => {
        if (!uploadSchool) { setAvailableClasses([]); return; }
        const qs = new URLSearchParams({ school: uploadSchool }).toString();
        authFetch(`${config.API_BASE_URL}/api/curriculum/classes?${qs}`)
            .then((r) => handleApiResponse(r, navigate))
            .then((data) => setAvailableClasses(data ? (data.classes || []) : []))
            .catch(() => setAvailableClasses([]));
    }, [uploadSchool, navigate]);

    // ── Fetch documents ──────────────────────────────────────────────────────
    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (schoolFilter) params.append('school', schoolFilter);
            if (classFilter) params.append('class_name', classFilter);
            const qs = params.toString();
            const res = await authFetch(
                `${config.API_BASE_URL}/api/curriculum/documents${qs ? `?${qs}` : ''}`
            );
            const data = await handleApiResponse(res, navigate);
            if (data === null) return; // 401 — already redirected
            setDocuments(data.documents || []);
        } catch (err) {
            setError(`Couldn't load curriculum documents. ${describeApiError(err)}`);
        } finally {
            setLoading(false);
        }
    }, [schoolFilter, classFilter, navigate]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // ── Filter dropdowns: DB schools + any schools seen in docs (union) ───────
    const filterSchools = [...new Set([
        ...availableSchools,
        ...documents.map((d) => d.school).filter(Boolean),
    ])].sort();
    const filterClasses = [...new Set([
        ...documents
            .filter((d) => !schoolFilter || d.school === schoolFilter)
            .map((d) => d.class_name)
            .filter(Boolean),
    ])].sort();

    // ── Upload handler ────────────────────────────────────────────────────────
    const handleUpload = async () => {
        if (!uploadFile || !uploadSchool) return;
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('school', uploadSchool);
            if (uploadClass) formData.append('class_name', uploadClass);

            const res = await authFetch(`${config.API_BASE_URL}/api/curriculum/upload`, {
                method: 'POST',
                body: formData,
            });
            const data = await handleApiResponse(res, navigate);
            if (data === null) return; // 401 — already redirected
            setUploadOpen(false);
            setUploadFile(null);
            setUploadSchool('');
            setUploadClass('');
            fetchDocuments();
        } catch (err) {
            setUploadError(describeApiError(err));
        } finally {
            setUploading(false);
        }
    };

    // ── Download handler ──────────────────────────────────────────────────────
    const handleDownload = async (doc) => {
        try {
            const res = await authFetch(`${config.API_BASE_URL}/api/curriculum/documents/${doc.id}/download`);
            if (res.status === 401) { navigate('/signin'); return; }
            if (!res.ok) throw new Error("This file couldn't be downloaded. Try again in a moment.");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        }
    };

    // ── Delete handler ────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await authFetch(`${config.API_BASE_URL}/api/curriculum/documents/${deleteTarget.id}`, {
                method: 'DELETE',
            });
            const data = await handleApiResponse(res, navigate);
            if (data === null) return; // 401 — already redirected
            setDeleteTarget(null);
            fetchDocuments();
        } catch (err) {
            setError(`This document couldn't be deleted. ${describeApiError(err)}`);
        } finally {
            setDeleting(false);
        }
    };

    // ── View handler ──────────────────────────────────────────────────────────
    const handleView = async (doc) => {
        setViewDoc(doc);
        setViewUrl(null);
        setViewText(null);
        setViewError(null);
        setViewLoading(true);
        try {
            const res = await authFetch(`${config.API_BASE_URL}/api/curriculum/documents/${doc.id}/download`);
            if (res.status === 401) { navigate('/signin'); return; }
            if (!res.ok) throw new Error("This file couldn't be opened. Try downloading it instead.");
            const blob = await res.blob();
            if (doc.content_type === 'text/plain') {
                setViewText(await blob.text());
            } else {
                setViewUrl(URL.createObjectURL(blob));
            }
        } catch (err) {
            setViewError(err.message);
        } finally {
            setViewLoading(false);
        }
    };

    const handleViewClose = () => {
        if (viewUrl) URL.revokeObjectURL(viewUrl);
        setViewDoc(null);
        setViewUrl(null);
        setViewText(null);
        setViewError(null);
    };

    // ── Open upload dialog (re-fetch schools for MASTER_ADMIN, classes for SCHOOL_ADMIN) ──
    const openUploadDialog = () => {
        const school = currentUser?.school || '';
        setUploadFile(null);
        setUploadSchool(school);
        setUploadClass('');
        setUploadError(null);
        setUploadOpen(true);

        // Always refresh schools live from DB for MASTER_ADMIN
        if (isMasterAdmin) {
            authFetch(`${config.API_BASE_URL}/api/curriculum/schools`)
                .then((r) => handleApiResponse(r, navigate))
                .then((data) => { if (data) setAvailableSchools(data.schools || []); })
                .catch(() => {});
        }

        // Always refresh classes for the pre-selected school when dialog opens (SCHOOL_ADMIN)
        if (school) {
            const qs = new URLSearchParams({ school }).toString();
            authFetch(`${config.API_BASE_URL}/api/curriculum/classes?${qs}`)
                .then((r) => handleApiResponse(r, navigate))
                .then((data) => setAvailableClasses(data ? (data.classes || []) : []))
                .catch(() => setAvailableClasses([]));
        } else {
            setAvailableClasses([]);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#18181b' }}>
                    Curriculum Documents
                </Typography>
                {isAdmin && (
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={openUploadDialog}
                        sx={{ textTransform: 'none' }}
                    >
                        Upload Document
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filter row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {isMasterAdmin && (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>School</InputLabel>
                        <Select
                            value={schoolFilter}
                            label="School"
                            onChange={(e) => { setSchoolFilter(e.target.value); setClassFilter(''); }}
                        >
                            <MenuItem value="">All Schools</MenuItem>
                            {filterSchools.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Class</InputLabel>
                    <Select
                        value={classFilter}
                        label="Class"
                        onChange={(e) => setClassFilter(e.target.value)}
                    >
                        <MenuItem value="">All Classes</MenuItem>
                        {filterClasses.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Documents table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : documents.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                        {isTeacherRole ? 'No curriculum documents yet' : 'No curriculum documents found'}
                    </Typography>
                    {isTeacherRole && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Upload one for your class with "Upload Document" above.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f7f7f8' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documents.map((doc) => {
                                const typeInfo = getFileTypeInfo(doc.content_type);
                                return (
                                    <TableRow key={doc.id} hover>
                                        <TableCell>
                                            <Chip
                                                icon={typeInfo.icon}
                                                label={typeInfo.label}
                                                size="small"
                                                sx={{
                                                    backgroundColor: `${typeInfo.color}15`,
                                                    color: typeInfo.color,
                                                    fontWeight: 500,
                                                    '& .MuiChip-icon': { color: typeInfo.color },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    color: '#4f46e5',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                                onClick={() => handleView(doc)}
                                            >
                                                {doc.filename}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{doc.school || '—'}</TableCell>
                                        <TableCell>{doc.class_name || '—'}</TableCell>
                                        <TableCell>{doc.uploaded_by || '—'}</TableCell>
                                        <TableCell>{formatDate(doc.upload_date)}</TableCell>
                                        <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View">
                                                <IconButton size="small" onClick={() => handleView(doc)} color="default">
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Download">
                                                <IconButton size="small" onClick={() => handleDownload(doc)} color="primary">
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {isAdmin && (
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => setDeleteTarget(doc)} color="error">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Upload Dialog ──────────────────────────────────────────────────── */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Upload Curriculum Document
                    <IconButton size="small" onClick={() => setUploadOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {uploadError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
                    )}

                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ mb: 2, py: 1.5, textTransform: 'none' }}
                    >
                        {uploadFile ? uploadFile.name : 'Choose File'}
                        <input
                            type="file"
                            hidden
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                            onChange={(e) => setUploadFile(e.target.files[0] || null)}
                        />
                    </Button>

                    {/* School — dropdown for MASTER_ADMIN, read-only field for SCHOOL_ADMIN/TEACHER */}
                    {isSchoolScoped ? (
                        <TextField
                            label="School"
                            value={uploadSchool}
                            fullWidth
                            required
                            size="small"
                            sx={{ mb: 2 }}
                            disabled
                        />
                    ) : (
                        <FormControl fullWidth required size="small" sx={{ mb: 2 }}>
                            <InputLabel>School</InputLabel>
                            <Select
                                value={uploadSchool}
                                label="School"
                                onChange={(e) => { setUploadSchool(e.target.value); setUploadClass(''); }}
                            >
                                {availableSchools.length === 0 && (
                                    <MenuItem value="" disabled>No schools found</MenuItem>
                                )}
                                {availableSchools.map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Class — dropdown populated from groups for selected school */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Class (optional)</InputLabel>
                        <Select
                            value={uploadClass}
                            label="Class (optional)"
                            onChange={(e) => setUploadClass(e.target.value)}
                            disabled={!uploadSchool}
                        >
                            <MenuItem value="">— None —</MenuItem>
                            {availableClasses.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={uploading || !uploadFile || !uploadSchool}
                        startIcon={uploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Document Viewer Dialog ────────────────────────────────────────── */}
            <Dialog
                open={!!viewDoc}
                onClose={handleViewClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                        {viewDoc && getFileTypeInfo(viewDoc.content_type).icon}
                        <Typography noWrap sx={{ fontWeight: 600 }}>{viewDoc?.filename}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <Tooltip title="Download">
                            <IconButton size="small" onClick={() => viewDoc && handleDownload(viewDoc)} color="primary">
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={handleViewClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {viewLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {viewError && (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="error">{viewError}</Alert>
                        </Box>
                    )}
                    {!viewLoading && !viewError && viewText !== null && (
                        <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
                            <Typography
                                component="pre"
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', m: 0 }}
                            >
                                {viewText}
                            </Typography>
                        </Box>
                    )}
                    {!viewLoading && !viewError && viewUrl !== null && (
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            {viewDoc?.content_type === 'application/pdf' ? (
                                <iframe
                                    src={viewUrl}
                                    title={viewDoc?.filename}
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            ) : viewDoc?.public_url?.startsWith('https://') ? (
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(viewDoc.public_url)}&embedded=true`}
                                    title={viewDoc?.filename}
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 4 }}>
                                    <DescriptionIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>Preview not available for this file type.</Typography>
                                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => viewDoc && handleDownload(viewDoc)}>
                                        Download to View
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Delete Confirmation Dialog ─────────────────────────────────────── */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete Document</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteTarget?.filename}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CurriculumPage;
